import AreaColapsavel from 'components/AreaColapsavel';
import Button from 'components/Form/Button';
import FormCore from 'components/Form/Core';
import { Icon } from 'components/Icon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Create as CreateContato,
  Desativar,
  MarcarPrincipal,
  ReadByCliente,
} from 'services/contato-cliente';
import { ResumoTelefones } from 'services/tentativa-cobranca';
import { exposeStrapiError, normalizeResponse } from 'utils';
import { formatPhone } from 'utils/parsers';
import {
  Badge,
  BarraTopo,
  ListaTelefones,
  NovoContatoContainer,
  TelefoneBadges,
  TelefoneDados,
  TelefoneLinha,
  TelefoneMeta,
  TelefoneNumero,
  TelefonesContainer,
  TituloBloco,
  VazioMensagem,
} from './styled';

const optionsTipo = [
  { id: 'celular', title: 'Celular' },
  { id: 'fixo', title: 'Fixo' },
  { id: 'comercial', title: 'Comercial' },
  { id: 'whatsapp', title: 'WhatsApp' },
  { id: 'email', title: 'E-mail' },
];

// contato-cliente e tentativa-cobranca usam vocabularios diferentes:
// o contato tem "comercial", a tentativa grava "empresa".
const tipoParaTentativa = {
  celular: 'celular',
  fixo: 'fixo',
  comercial: 'empresa',
  whatsapp: 'celular',
};

const somenteDigitos = (valor) => `${valor || ''}`.replace(/\D/g, '');

export const Telefones = ({
  clienteId,
  cliente,
  selecionado,
  onSelecionar,
  onTotal,
  onChange,
}) => {
  const [contatos, setContatos] = useState([]);
  const [resumo, setResumo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [criando, setCriando] = useState(false);
  const [formRegister, setFormRegister] = useState({});
  const formRef = useRef(null);

  const init = useCallback(async () => {
    if (!clienteId) {
      return;
    }
    setLoading(true);
    try {
      const [resultContatos, resultResumo] = await Promise.all([
        ReadByCliente(clienteId),
        ResumoTelefones({ cliente: clienteId }),
      ]);
      setContatos(normalizeResponse(resultContatos));
      // resumo-telefones nao usa o envelope { results, pagination }
      setResumo(resultResumo?.telefones || []);
    } catch (err) {
      console.error('Erro ao carregar telefones do cliente:', err);
      toast.error('Erro ao carregar os telefones');
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => init(), [init]);

  // Junta o cadastro do cliente com os contatos avulsos e cruza cada numero
  // com a efetividade apurada nas tentativas. Chave: so os digitos.
  const lista = useMemo(() => {
    const porChave = new Map();

    const adiciona = (item) => {
      const chave =
        item.tipo === 'email'
          ? `email:${`${item.valor}`.toLowerCase()}`
          : somenteDigitos(item.valor);
      if (!chave) {
        return;
      }
      const atual = porChave.get(chave);
      porChave.set(chave, { ...(atual || {}), ...item, chave });
    };

    // 1. cadastro do cliente
    [
      { campo: 'telefone_celular', tipo: 'celular', rotulo: 'cadastro' },
      { campo: 'telefone_fixo', tipo: 'fixo', rotulo: 'cadastro' },
      { campo: 'telefone_empresa', tipo: 'comercial', rotulo: 'cadastro' },
    ].forEach(({ campo, tipo, rotulo }) => {
      if (cliente?.[campo]) {
        adiciona({ valor: cliente[campo], tipo, rotulo, doCadastro: true });
      }
    });

    // 2. contatos cadastrados pelo operador (sobrepoem o rotulo do cadastro)
    (contatos || [])
      .filter((item) => item?.ativo !== false)
      .forEach((item) => {
        adiciona({
          id: item?.id,
          valor: item?.valor,
          tipo: item?.tipo,
          rotulo: item?.rotulo,
          observacao: item?.observacao,
          principal: item?.principal,
        });
      });

    // 3. numeros que so aparecem no historico de tentativas
    (resumo || []).forEach((item) => {
      const chave = somenteDigitos(item?.telefone);
      if (!chave) {
        return;
      }
      const atual = porChave.get(chave);
      porChave.set(chave, {
        valor: item?.telefone,
        tipo: 'outro',
        ...(atual || {}),
        chave,
        stats: item,
      });
    });

    return Array.from(porChave.values()).sort((a, b) => {
      // improdutivo primeiro nao ajuda quem vai ligar: melhores no topo
      if (!!a.stats?.improdutivo !== !!b.stats?.improdutivo) {
        return a.stats?.improdutivo ? 1 : -1;
      }
      if (!!a.principal !== !!b.principal) {
        return a.principal ? -1 : 1;
      }
      return (
        (b.stats?.contatos_efetivos || 0) - (a.stats?.contatos_efetivos || 0)
      );
    });
  }, [cliente, contatos, resumo]);

  // alimenta o contador da aba
  useEffect(() => onTotal?.(lista.length), [lista, onTotal]);

  const formNovoContato = [
    {
      ref: 'tipo',
      placeholder: 'Tipo',
      options: optionsTipo,
      required: true,
      half: true,
    },
    {
      ref: 'valor',
      placeholder: 'Telefone ou e-mail',
      required: true,
      half: true,
    },
    { ref: 'rotulo', placeholder: 'Rótulo', half: true },
    {
      ref: 'principal',
      placeholder: 'Principal',
      options: [
        { id: 'nao', title: 'Não' },
        { id: 'sim', title: 'Sim' },
      ],
      half: true,
    },
    { ref: 'observacao', placeholder: 'Observação', full: true },
  ];

  const salvar = async () => {
    const form = formRef.current?.getForm?.();
    if (!form) {
      return;
    }

    setSalvando(true);
    try {
      const payload = {
        cliente: clienteId,
        tipo: form?.tipo,
        valor: form?.valor,
        rotulo: form?.rotulo || null,
        principal: form?.principal === 'sim',
        observacao: form?.observacao || null,
      };

      const result = await CreateContato({ data: payload });
      if (exposeStrapiError(result)) {
        return;
      }

      toast.success('Contato cadastrado com sucesso!');
      setFormRegister({});
      setCriando(false);
      await init();
      onChange?.();
    } catch (err) {
      console.error('Erro ao cadastrar contato:', err);
      toast.error('Erro ao cadastrar o contato');
    } finally {
      setSalvando(false);
    }
  };

  const acao = async (executa, mensagem) => {
    setSalvando(true);
    try {
      const result = await executa();
      if (exposeStrapiError(result)) {
        return;
      }
      toast.success(mensagem);
      await init();
      onChange?.();
    } catch (err) {
      console.error('Erro ao atualizar contato:', err);
      toast.error('Erro ao atualizar o contato');
    } finally {
      setSalvando(false);
    }
  };

  const selecionar = (item) => {
    if (item.tipo === 'email') {
      return;
    }
    onSelecionar?.({
      telefone: somenteDigitos(item.valor),
      tipo: tipoParaTentativa[item.tipo] || 'outro',
    });
  };

  return (
    <TelefonesContainer>
      <BarraTopo>
        <TituloBloco>Telefones e contatos</TituloBloco>
        <Button
          color={criando ? 'lightgrey' : 'blue'}
          small
          nospace
          width="auto"
          onClick={() => setCriando(!criando)}
        >
          {criando ? 'Cancelar' : '+ Contato'}
        </Button>
      </BarraTopo>

      <AreaColapsavel aberto={criando} vao="8px">
        <NovoContatoContainer>
          <FormCore
            ref={formRef}
            formItems={formNovoContato}
            register={formRegister}
          />
          <Button
            color="blue"
            small
            nospace
            width="auto"
            disabled={salvando}
            onClick={salvar}
          >
            {salvando ? 'Salvando...' : 'Cadastrar contato'}
          </Button>
        </NovoContatoContainer>
      </AreaColapsavel>

      {!lista.length ? (
        <VazioMensagem>
          {loading
            ? 'Carregando contatos...'
            : 'Nenhum telefone conhecido para este cliente.'}
        </VazioMensagem>
      ) : (
        <ListaTelefones>
          {lista.map((item) => {
            const ehEmail = item.tipo === 'email';
            const stats = item.stats;
            const numero = ehEmail
              ? item.valor
              : formatPhone(null, item.valor) || item.valor;
            const ativo =
              !ehEmail && selecionado === somenteDigitos(item.valor);

            return (
              <TelefoneLinha
                key={item.chave}
                $ativo={ativo}
                $clicavel={!ehEmail}
                onClick={() => selecionar(item)}
                title={ehEmail ? undefined : 'Usar este número na tentativa'}
              >
                <Icon
                  name={ehEmail ? 'mail' : 'call'}
                  size={18}
                  color={stats?.improdutivo ? '#FF3B30' : '#a4a4a4'}
                />
                <TelefoneDados>
                  <TelefoneNumero $improdutivo={stats?.improdutivo}>
                    {numero}
                  </TelefoneNumero>
                  <TelefoneMeta>
                    {[
                      item.rotulo,
                      item.tipo,
                      stats
                        ? `${stats.tentativas} tentativa(s), ${stats.contatos_efetivos} efetivo(s)`
                        : 'sem tentativas',
                      item.observacao,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </TelefoneMeta>
                </TelefoneDados>
                <TelefoneBadges>
                  {item.principal ? (
                    <Badge color="#2670D1" $solido>
                      Principal
                    </Badge>
                  ) : null}
                  {stats?.improdutivo ? (
                    <Badge color="#FF3B30" $solido>
                      Improdutivo
                    </Badge>
                  ) : null}
                  {item.id && !item.principal && !ehEmail ? (
                    <Icon
                      name="push_pin"
                      size={18}
                      color="#a4a4a4"
                      onClick={(e) => {
                        e.stopPropagation();
                        acao(
                          () => MarcarPrincipal(item.id),
                          'Contato marcado como principal.'
                        );
                      }}
                      elementAttributes={{
                        title:
                          'Marcar como principal (atualiza o cadastro do cliente)',
                        style: { cursor: 'pointer' },
                      }}
                    />
                  ) : null}
                  {item.id ? (
                    <Icon
                      name="do_not_disturb_on"
                      size={18}
                      color="#a4a4a4"
                      onClick={(e) => {
                        e.stopPropagation();
                        acao(() => Desativar(item.id), 'Contato desativado.');
                      }}
                      elementAttributes={{
                        title: 'Desativar contato',
                        style: { cursor: 'pointer' },
                      }}
                    />
                  ) : null}
                </TelefoneBadges>
              </TelefoneLinha>
            );
          })}
        </ListaTelefones>
      )}
    </TelefonesContainer>
  );
};
